/**
 * Lazy playback for "Also explore" cards.
 *
 * The markup ships preload="none" and no autoplay attribute, so nothing is
 * fetched until a card is on screen — these are full-resolution masters (the
 * toothbrush clip is 8.5 MB) and loading one costs more than the rest of the
 * page. A card that scrolls away pauses, so several never decode at once.
 *
 * On screen means half the card in view for a moment, not a card coming
 * within 100px of the window: a shopper scrolling past the row on the way to
 * the footer used to start the whole download for a clip they never saw.
 *
 * Nothing is fetched at all where the clip would cost more than it gives:
 * the browser asking to save data, a 2G connection, or reduced motion (which
 * used to fetch the whole file and then not play it). The card is still a
 * card with its name and its link.
 *
 * Without this the cards still render: the poster frame shows (where there is
 * one) and the link works. Only playback is lost.
 */
const videos = document.querySelectorAll('[data-explore-video]');

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const connection = navigator.connection;
const lean = Boolean(connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || '')));

if (videos.length && 'IntersectionObserver' in window && !reduced && !lean) {
  // How long a card has to stay in view before its file is asked for.
  const DWELL = 600;
  const timers = new WeakMap();

  const start = (video) => {
    if (video.preload === 'none') video.preload = 'auto';
    video.play?.().catch(() => {});
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        clearTimeout(timers.get(video));
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          // Already loaded (it played before and was paused): resume at once.
          timers.set(video, setTimeout(() => start(video), video.readyState > 0 ? 0 : DWELL));
        } else {
          video.pause?.();
        }
      }
    },
    { threshold: [0, 0.5] }
  );

  for (const video of videos) observer.observe(video);
}
